import React from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { StatusBadge } from '@/components/common/StatusBadge';

export interface ModulePlaceholderProps {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  badge?: string;
  children?: React.ReactNode;
}

export const ModulePlaceholder: React.FC<ModulePlaceholderProps> = ({
  title,
  subtitle,
  icon: Icon,
  badge,
  children,
}) => {
  const { orgName, orgStatus } = useAuthContext();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-zinc-800">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <NavLink
              to="/"
              className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1 transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Overview</span>
            </NavLink>
            <span className="text-zinc-700">•</span>
            <span className="text-xs font-mono text-zinc-400">{orgName || 'Workspace'}</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-white">{title}</h1>
                {badge && (
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                    {badge}
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-400 mt-0.5">{subtitle}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <StatusBadge status={orgStatus} size="sm" />
        </div>
      </div>

      {children ? (
        children
      ) : (
        <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl p-8 text-center max-w-xl mx-auto my-12">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-semibold text-white">Partner Workspace Ready</h2>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
            This module is connected to your partner account. Share your referral link to begin earning recurring commissions.
          </p>
        </div>
      )}
    </div>
  );
};

export default ModulePlaceholder;
